import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Switch} from '@/web-components';
import {HorizontalFlexWrapper} from './app_salesforce_style';
import {SelectionElementLabel} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import DirectApi from '../../../../directApi';
import {createToast} from '../../../shared/components/toasts/toast';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';

const LinkedFieldsWrapper = styled.div`
	margin: 8px 0;
`;

export const SALESFORCE_LINKED_FIELD_ORDER = ['opportunityName', 'opportunityAmount', 'opportunityDates'];

const SALESFORCE_LINKED_FIELD_LABEL_IDS = {
	opportunityName: 'settings.app_catalog.salesforce.sync_direction.opportunity_name',
	opportunityAmount: 'settings.app_catalog.salesforce.sync_direction.opportunity_amount',
	opportunityDates: 'settings.app_catalog.salesforce.sync_direction.opportunity_dates',
};

const readLinkedFromRaw = (raw, field) => {
	// flat format: {opportunityName: false, ...}
	if (raw && typeof raw[field] === 'boolean') {
		return raw[field];
	}
	// wrapped format: {linkedFields: {field: bool | {linked: bool}}}
	const linkedFields = raw?.linkedFields;
	if (!linkedFields || typeof linkedFields !== 'object') {
		return true;
	}
	const entry = linkedFields[field];
	if (entry == null) {
		return true;
	}
	if (typeof entry === 'object' && typeof entry.linked === 'boolean') {
		return entry.linked;
	}
	return Boolean(entry);
};

const fetchPutJson = (endpoint, body) => {
	const headers = new Headers();
	headers.append('Content-Type', 'application/json');
	return fetch(DirectApi.graphqlServerEndpoint(endpoint), {
		headers,
		credentials: 'include',
		method: 'PUT',
		body: JSON.stringify(body),
	});
};

const SalesforceEnabledFieldsSettings = ({onLinkedFieldsUpdated}) => {
	const intl = useIntl();
	const [linkedFields, setLinkedFields] = useState(null);
	const linkedFieldsRef = useRef(linkedFields);
	linkedFieldsRef.current = linkedFields;
	const initStartedRef = useRef(false);
	const onLinkedFieldsUpdatedRef = useRef(onLinkedFieldsUpdated);
	onLinkedFieldsUpdatedRef.current = onLinkedFieldsUpdated;

	useEffect(() => {
		if (initStartedRef.current) {
			return undefined;
		}
		initStartedRef.current = true;
		let cancelled = false;

		DirectApi.Fetch_Raw('salesforce/linked_fields')
			.then(res => (res.ok ? res.json() : {}))
			.catch(() => ({}))
			.then(raw => {
				if (cancelled) {
					return;
				}
				const fields = {};
				for (const field of SALESFORCE_LINKED_FIELD_ORDER) {
					fields[field] = readLinkedFromRaw(raw, field);
				}
				setLinkedFields(fields);
				onLinkedFieldsUpdatedRef.current?.(fields);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const handleToggle = useCallback(
		(field, previousChecked) => {
			const next = {...linkedFieldsRef.current, [field]: !previousChecked};
			setLinkedFields(next);
			onLinkedFieldsUpdatedRef.current?.(next);
			const body = {};
			for (const f of SALESFORCE_LINKED_FIELD_ORDER) {
				body[f] = next[f];
			}
			fetchPutJson('salesforce/linked_fields', body)
				.then(() => {
					createToast({duration: 2000, message: intl.formatMessage({id: 'common.changes_saved'})});
				})
				.catch(() => {});
		},
		[intl]
	);

	if (!linkedFields) {
		return <InlineLoader />;
	}

	return (
		<>
			{SALESFORCE_LINKED_FIELD_ORDER.map(field => (
				<LinkedFieldsWrapper key={field}>
					<HorizontalFlexWrapper>
						<Switch
							checked={linkedFields[field]}
							onChange={previousChecked => handleToggle(field, previousChecked)}
							cy={`salesforce-enabled-field-${field}`}
						/>
						<SelectionElementLabel>
							{intl.formatMessage({id: SALESFORCE_LINKED_FIELD_LABEL_IDS[field]})}
						</SelectionElementLabel>
					</HorizontalFlexWrapper>
				</LinkedFieldsWrapper>
			))}
		</>
	);
};

export default SalesforceEnabledFieldsSettings;
